/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * CART SYNC FOR LOGGED-IN USERS
 * ------------------------------------------------------------------------------------------------------------
 * 
 * This script ensures that when a user logs in, their browser cart is merged with their account cart,
 * allowing cart synchronization across different browsers and devices.
 */

(function() {
  'use strict';

  // Track if cart sync has already been performed to avoid duplicate syncs
  let cartSyncPerformed = false;
  let previousCustomerState = null;
  const SYNC_STORAGE_KEY = 'cart_sync_performed';
  const SYNC_TIMESTAMP_KEY = 'cart_sync_timestamp';
  const CART_STATE_KEY = 'cart_state_hash'; // Track cart state to detect changes
  let cartRefreshInProgress = false;

  /**
   * Check if user is logged in by checking for customer data
   */
  async function isCustomerLoggedIn() {
    // Method 1: Check global variable set by theme.liquid
    if (typeof window.customerLoggedIn !== 'undefined') {
      return window.customerLoggedIn === true;
    }

    // Method 2: Check if we're on a customer account page
    const isAccountPage = window.location.pathname.includes('/account') && 
                          !window.location.pathname.includes('/account/login') &&
                          !window.location.pathname.includes('/account/register');
    
    if (isAccountPage) {
      return true;
    }

    // Method 3: Check for customer-specific elements in the DOM
    const hasCustomerElements = document.querySelector('.account') || 
                                document.querySelector('[data-customer-id]') ||
                                document.body.classList.contains('customer-logged-in');

    if (hasCustomerElements) {
      return true;
    }

    // Method 4: Check cart API - if user is logged in, cart will be associated with account
    try {
      const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart.js';
      const response = await fetch(cartUrl);
      if (response.ok) {
        const cart = await response.json();
        // If cart has a customer association, user is logged in
        // Note: This is a fallback method
        return cart.token !== undefined;
      }
    } catch (error) {
      // Silently fail
    }

    return false;
  }

  /**
   * Create a hash of cart state for comparison
   */
  function getCartStateHash(cart) {
    if (!cart || !cart.items) return '';
    const items = cart.items.map(item => 
      `${item.variant_id}:${item.quantity}:${item.selling_plan_allocation?.selling_plan?.id || ''}`
    ).sort().join('|');
    return `${cart.item_count}_${items}`;
  }

  /**
   * Get current cart from Shopify
   * Includes credentials to ensure we get the account cart for logged-in users
   */
  async function getCurrentCart(forceRefresh = false) {
    try {
      const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart.js';
      
      // Add timestamp to prevent caching
      const url = forceRefresh ? `${cartUrl}?t=${Date.now()}` : cartUrl;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin', // Include cookies to get account cart
        cache: 'no-store', // Always fetch fresh, never use cache
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const cart = await response.json();
        
        // Store cart state hash for comparison
        const stateHash = getCartStateHash(cart);
        try {
          localStorage.setItem(CART_STATE_KEY, stateHash);
        } catch (e) {
          // localStorage might not be available
        }
        
        return cart;
      }
      return null;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Add item to cart
   */
  async function addItemToCart(variantId, quantity, properties = {}, sellingPlanId = null) {
    try {
      const cartAddUrl = window.themeVariables?.routes?.cartAddUrl || '/cart/add.js';
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      if (sellingPlanId) {
        formData.append('selling_plan', sellingPlanId);
      }

      // Add properties if any
      if (properties && Object.keys(properties).length > 0) {
        Object.keys(properties).forEach(key => {
          formData.append(`properties[${key}]`, properties[key]);
        });
      }

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin', // Include cookies to ensure account cart is used
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json();
        console.error('Error adding item to cart:', errorData);
        return null;
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      return null;
    }
  }

  /**
   * Check if cart sync has been performed recently (within last hour)
   */
  function hasRecentSync() {
    try {
      const syncTimestamp = localStorage.getItem(SYNC_TIMESTAMP_KEY);
      if (!syncTimestamp) {
        return false;
      }
      
      const timestamp = parseInt(syncTimestamp, 10);
      const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
      const now = Date.now();
      
      // If sync was performed within the last hour, consider it recent
      return (now - timestamp) < oneHour;
    } catch (error) {
      return false;
    }
  }

  /**
   * Mark cart sync as performed
   */
  function markSyncPerformed() {
    cartSyncPerformed = true;
    try {
      localStorage.setItem(SYNC_STORAGE_KEY, 'true');
      localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      // localStorage might not be available, that's okay
    }
  }

  /**
   * Reset sync state (when user logs out)
   */
  function resetSyncState() {
    cartSyncPerformed = false;
    try {
      localStorage.removeItem(SYNC_STORAGE_KEY);
      localStorage.removeItem(SYNC_TIMESTAMP_KEY);
    } catch (error) {
      // localStorage might not be available, that's okay
    }
  }

  /**
   * Merge browser cart with account cart
   */
  async function syncCartOnLogin() {
    // Prevent duplicate syncs
    if (cartSyncPerformed || hasRecentSync()) {
      return;
    }

    // Check if user is logged in
    const isLoggedIn = await isCustomerLoggedIn();
    if (!isLoggedIn) {
      return;
    }

    try {
      // Get current cart
      const currentCart = await getCurrentCart();
      
      if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
        // No items to sync, but mark as performed to avoid repeated checks
        markSyncPerformed();
        return;
      }

      // Store cart items before sync
      const itemsToSync = currentCart.items.map(item => ({
        variantId: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
        sellingPlanId: item.selling_plan_allocation?.selling_plan?.id || null
      }));

      // Add each item to the account cart
      // Note: Shopify will automatically merge items with the same variant and properties
      // We add items one by one to ensure proper merging
      let syncSuccess = true;
      let addedCount = 0;
      
      for (const item of itemsToSync) {
        // Small delay between additions to avoid rate limiting
        if (addedCount > 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        const result = await addItemToCart(
          item.variantId,
          item.quantity,
          item.properties,
          item.sellingPlanId
        );
        
        if (result) {
          addedCount++;
        } else {
          syncSuccess = false;
          console.warn('Failed to sync cart item:', item);
        }
      }

      // Mark sync as performed even if some items failed (to avoid infinite retries)
      markSyncPerformed();
      
      if (syncSuccess && addedCount > 0) {
        // Refresh the cart display after a short delay
        setTimeout(() => {
          refreshCartDisplay();
        }, 800);
      }
    } catch (error) {
      console.error('Error syncing cart on login:', error);
      // Mark as performed to avoid infinite retries on errors
      markSyncPerformed();
    }
  }

  /**
   * Refresh cart display after sync - always fetches from server for logged-in users
   */
  async function refreshCartDisplay() {
    if (cartRefreshInProgress) return; // Prevent overlapping refreshes
    cartRefreshInProgress = true;
    
    try {
      // Always fetch fresh cart from server (this gets account cart for logged-in users)
      const updatedCart = await getCurrentCart(true); // Force refresh
      
      if (updatedCart) {
        // Update cart count in header immediately
        const cartCountElements = document.querySelectorAll('cart-count');
        cartCountElements.forEach(el => {
          if (el.textContent !== updatedCart.item_count.toString()) {
            el.textContent = updatedCart.item_count;
          }
        });

        // Trigger cart updated event with fresh data
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: updatedCart }
        }));

        // Get cart sections for mini-cart refresh
        const sectionId = document.querySelector('cart-drawer')?.getAttribute('section');
        if (sectionId && window.themeVariables?.routes?.cartUrl) {
          try {
            const response = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${sectionId}&t=${Date.now()}`, {
              credentials: 'same-origin', // Include cookies to get account cart
              cache: 'no-store', // Always fetch fresh
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            });
            const html = await response.text();
            updatedCart.sections = { [sectionId]: html };
            
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { cart: updatedCart }
            }));
          } catch (error) {
            console.error('Error fetching cart sections:', error);
            // Still trigger refresh without sections
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { cart: updatedCart }
            }));
          }
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: updatedCart }
          }));
        }
      }
    } catch (error) {
      console.error('Error refreshing cart display:', error);
      // Fallback: trigger basic refresh
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
    } finally {
      cartRefreshInProgress = false;
    }
  }

  /**
   * Monitor for login state changes
   */
  async function monitorLoginState() {
    // Check if sync was already performed (from localStorage)
    try {
      const storedSync = localStorage.getItem(SYNC_STORAGE_KEY);
      if (storedSync === 'true' && hasRecentSync()) {
        cartSyncPerformed = true;
      }
    } catch (error) {
      // localStorage might not be available
    }

    // Check initial state
    const currentCustomerState = await isCustomerLoggedIn();
    previousCustomerState = currentCustomerState;

    // If user is already logged in on page load, sync cart
    if (currentCustomerState && !cartSyncPerformed) {
      // Wait a bit for page to fully load
      setTimeout(() => {
        syncCartOnLogin();
      }, 1500);
    }

    // Monitor for changes in customer state (login/logout)
    const checkInterval = setInterval(async () => {
      const currentState = await isCustomerLoggedIn();
      
      // If customer just logged in (state changed from false to true)
      if (!previousCustomerState && currentState) {
        previousCustomerState = currentState;
        resetSyncState(); // Reset flag to allow sync
        cartSyncPerformed = false;
        syncCartOnLogin();
      } else if (previousCustomerState !== currentState) {
        previousCustomerState = currentState;
        // If user logged out, reset sync flag
        if (!currentState) {
          resetSyncState();
          cartSyncPerformed = false;
        }
      }
    }, 2000);

    // Stop monitoring after 5 minutes to avoid unnecessary checks
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 300000);
  }

  /**
   * Monitor for successful login form submission
   */
  function monitorLoginForm() {
    // Listen for successful login form submissions
    document.addEventListener('submit', async function(event) {
      const form = event.target;
      
      // Check if this is a customer login form
      if (form && (form.name === 'login' || (form.action && form.action.includes('login')))) {
        // Wait for form submission to complete (login redirects, so we check after redirect)
        setTimeout(async () => {
          // Check if login was successful (user is now logged in)
          if (await isCustomerLoggedIn()) {
            resetSyncState(); // Reset flag
            cartSyncPerformed = false;
            syncCartOnLogin();
          }
        }, 2000);
      }
    });

    // Also listen for page navigation after login (in case of redirect)
    let lastUrl = location.href;
    new MutationObserver(() => {
      const currentUrl = location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        // If URL changed and user is logged in, sync cart
        isCustomerLoggedIn().then(isLoggedIn => {
          if (isLoggedIn && !cartSyncPerformed && !hasRecentSync()) {
            setTimeout(() => {
              syncCartOnLogin();
            }, 1000);
          }
        });
      }
    }).observe(document, { subtree: true, childList: true });
  }

  /**
   * Periodically refresh cart for logged-in users to sync across browsers
   */
  function startCartRefreshInterval() {
    // Refresh cart every 5 seconds for logged-in users (very frequent for better sync)
    setInterval(async () => {
      if (cartRefreshInProgress) return; // Prevent overlapping refreshes
      
      const isLoggedIn = await isCustomerLoggedIn();
      if (isLoggedIn) {
        // Get stored cart state
        let previousStateHash = '';
        try {
          previousStateHash = localStorage.getItem(CART_STATE_KEY) || '';
        } catch (e) {
          // localStorage might not be available
        }
        
        // Always fetch latest cart from server to get account cart state
        const latestCart = await getCurrentCart(true); // Force refresh
        if (latestCart) {
          const currentStateHash = getCartStateHash(latestCart);
          
          // Check if cart has changed
          const currentItemCount = document.querySelector('cart-count')?.textContent?.trim();
          const latestItemCount = latestCart.item_count?.toString();
          const countChanged = currentItemCount !== latestItemCount;
          const stateChanged = previousStateHash !== currentStateHash;
          
          // If cart changed, refresh the display
          if (countChanged || stateChanged) {
            refreshCartDisplay();
          }
        }
      }
    }, 5000); // Check every 5 seconds
  }

  /**
   * Listen for cart updates and ensure we're using account cart
   */
  function setupCartUpdateListener() {
    // Listen for cart:updated events - force refresh for logged-in users
    document.addEventListener('cart:updated', async (event) => {
      const isLoggedIn = await isCustomerLoggedIn();
      if (isLoggedIn) {
        // Always refresh cart from server after any update for logged-in users
        // This ensures we get the account cart, not just browser cart
        // Use multiple timeouts to catch async updates
        setTimeout(() => {
          refreshCartDisplay();
        }, 500);
        setTimeout(() => {
          refreshCartDisplay();
        }, 2000);
      }
    });

    // Listen for variant:added - force refresh for logged-in users
    document.addEventListener('variant:added', async () => {
      const isLoggedIn = await isCustomerLoggedIn();
      if (isLoggedIn) {
        // Refresh cart after a delay to get account cart state
        // Do it twice - once quickly, once after a longer delay to catch any async updates
        setTimeout(() => {
          refreshCartDisplay();
        }, 1000);
        setTimeout(() => {
          refreshCartDisplay();
        }, 3000);
      }
    });

    // Intercept cart change operations (quantity updates, removals)
    document.addEventListener('click', async (event) => {
      const target = event.target.closest('a[href*="/cart/change"], a[href*="/cart/update"], a[href*="/cart/clear"]');
      if (target) {
        const isLoggedIn = await isCustomerLoggedIn();
        if (isLoggedIn) {
          // After cart change, refresh to get account cart
          setTimeout(() => {
            refreshCartDisplay();
          }, 1000);
        }
      }
    }, true); // Use capture phase to catch before navigation

    // Refresh cart when page becomes visible (user switches back from another tab/browser)
    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden) {
        // Page became visible - check if user is logged in and refresh cart
        const isLoggedIn = await isCustomerLoggedIn();
        if (isLoggedIn) {
          // Small delay to avoid too frequent refreshes
          setTimeout(() => {
            refreshCartDisplay();
          }, 1000);
        }
      }
    });

    // Also refresh cart when window gains focus (user switches back to this window)
    window.addEventListener('focus', async () => {
      const isLoggedIn = await isCustomerLoggedIn();
      if (isLoggedIn) {
        setTimeout(() => {
          refreshCartDisplay();
        }, 1000);
      }
    });
  }

  /**
   * Intercept fetch calls to cart endpoints to ensure proper syncing
   */
  function interceptCartAPI() {
    // Store original fetch
    const originalFetch = window.fetch;
    
    // Override fetch to intercept cart API calls
    window.fetch = async function(...args) {
      const url = args[0];
      const options = args[1] || {};
      
      // Check if this is a cart API call
      const isCartCall = typeof url === 'string' && (
        url.includes('/cart.js') ||
        url.includes('/cart/add.js') ||
        url.includes('/cart/update.js') ||
        url.includes('/cart/change.js') ||
        url.includes('/cart/clear.js')
      );
      
      if (isCartCall) {
        // Ensure credentials are included for logged-in users
        options.credentials = options.credentials || 'same-origin';
        options.cache = 'no-store';
        
        // Add cache-busting headers
        if (!options.headers) {
          options.headers = {};
        }
        options.headers['Cache-Control'] = 'no-cache';
        options.headers['Pragma'] = 'no-cache';
        
        // Add timestamp to URL to prevent caching
        if (typeof url === 'string' && !url.includes('?t=')) {
          args[0] = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
        }
        
        // Call original fetch
        const response = await originalFetch.apply(this, args);
        
        // If user is logged in and this was a cart operation, refresh cart after a delay
        if (response.ok) {
          isCustomerLoggedIn().then(isLoggedIn => {
            if (isLoggedIn) {
              setTimeout(() => {
                refreshCartDisplay();
              }, 1000);
            }
          });
        }
        
        return response;
      }
      
      // For non-cart calls, use original fetch
      return originalFetch.apply(this, args);
    };
  }

  /**
   * Initialize cart sync functionality
   */
  function initCartSync() {
    // Wait for theme variables to be available
    if (typeof window.themeVariables === 'undefined') {
      // Retry after a short delay
      setTimeout(initCartSync, 500);
      return;
    }

    // Intercept cart API calls first
    interceptCartAPI();

    // Start monitoring
    monitorLoginState();
    monitorLoginForm();
    setupCartUpdateListener();
    startCartRefreshInterval();

    // Also sync on page load if user is already logged in
    const initializeCartSync = async () => {
      const isLoggedIn = await isCustomerLoggedIn();
      if (isLoggedIn) {
        // Step 1: Immediately fetch account cart to see what's there
        const accountCart = await getCurrentCart(true);
        
        // Step 2: Get current browser cart
        const browserCart = await getCurrentCart(false);
        
        // Step 3: If carts differ, we need to sync
        if (accountCart && browserCart) {
          const accountHash = getCartStateHash(accountCart);
          const browserHash = getCartStateHash(browserCart);
          
          if (accountHash !== browserHash) {
            // Carts are different - refresh to get account cart
            refreshCartDisplay();
            
            // If browser cart has items not in account cart, sync them
            if (browserCart.items && browserCart.items.length > 0) {
              setTimeout(() => {
                syncCartOnLogin();
              }, 2000);
            }
          } else {
            // Carts match, but still refresh to ensure UI is updated
            refreshCartDisplay();
          }
        } else {
          // Fallback: just refresh
          refreshCartDisplay();
        }
        
        // Step 4: Set up continuous monitoring
        setTimeout(() => {
          refreshCartDisplay();
        }, 4000);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(initializeCartSync, 1000);
      });
    } else {
      setTimeout(initializeCartSync, 1000);
    }
  }

  // Initialize when script loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartSync);
  } else {
    initCartSync();
  }
})();